import { authFetch } from './authFetch';

const BASE = '/api/admin';

async function api(path: string, opts?: RequestInit) {
  const res = await authFetch(`${BASE}${path}`, opts);
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export interface SessionRow {
  id: string;
  userId: string;
  claudeSessionId: string | null;
  status: string;
  totalCostUsd: number | null;
  numTurns: number;
  createdAt: string;
  updatedAt: string;
  messages: Array<{ id: string; role: string; text: string; createdAt: string }>;
}

export interface StatsData {
  totalSessions: number;
  activeSessions: number;
  totalCostUsd: number;
  totalMessages: number;
}

export interface UserRow {
  userId: string;
  email: string | null;
  displayName: string | null;
  lastSeenAt: string | null;
  totalSessions: string;
  totalCostUsd: string;
  tier?: string;
  simulatedTier?: string | null;
  /** Admin demo override for the credits balance (centicredits) — clone of simulatedTier. */
  simulatedBalance?: number | null;
  hasByokClaude?: boolean;
  hasByokGemini?: boolean;
  claudeAuth?: 'byok' | 'sdk_oauth' | 'none';
  geminiAuth?: 'byok_ai_studio' | 'service_account' | 'operator_oauth' | 'none';
}

export const fetchStats = (): Promise<StatsData> => api('/stats');

export const fetchSessions = (
  filters?: Record<string, string>,
): Promise<{ sessions: SessionRow[]; total: number }> => {
  const params = new URLSearchParams(filters || {});
  return api(`/sessions?${params}`);
};

export const fetchSession = (id: string): Promise<SessionRow> =>
  api(`/sessions/${id}`);

export const killSession = (id: string) =>
  api(`/sessions/${id}/kill`, { method: 'POST' });

export const resumeSession = (id: string) =>
  api(`/sessions/${id}/resume`, { method: 'POST' });

export const fetchUsers = (): Promise<UserRow[]> => api('/users');

// ── Tier Quotas ──────────────────────────────────────────

export interface TierQuotaRow {
  id: string;
  tier: string;
  featureKey: string;
  limitValue: number | null;
  enabled: boolean;
  period: string;
  description: string | null;
}

export interface TierQuotasResponse {
  quotas: Record<string, TierQuotaRow[]>;
  featureKeys: string[];
}

export const fetchTierQuotas = (): Promise<TierQuotasResponse> => api('/tier-quotas');

export const upsertTierQuota = (data: {
  tier: string;
  featureKey: string;
  limitValue: number | null;
  enabled?: boolean;
  period?: string;
  description?: string;
}) =>
  api('/tier-quotas', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const deleteFeatureQuota = (featureKey: string) =>
  api(`/tier-quotas/${encodeURIComponent(featureKey)}`, { method: 'DELETE' });

// ── Admin Grants ──────────────────────────────────────────

export interface AdminGrantRow {
  email: string;
  grantedBy: string;
  grantedAt: string;
  revokedAt: string | null;
}

export const fetchGrants = (): Promise<AdminGrantRow[]> => api('/grants');

export const addGrant = (email: string) =>
  api('/grants', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

export const revokeGrant = (email: string) =>
  api(`/grants/${encodeURIComponent(email)}`, { method: 'DELETE' });

// ── AKB Knowledge Content ─────────────────────────────────

export interface AkbContentRow {
  id: string;
  title: string;
  body: string;
  scopeEnv: string | null;
  scopeTier: string | null;
  userId: string | null;
  priority: number;
  adminOnly: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AkbContentFilter {
  scopeEnv?: string;
  scopeTier?: string;
  adminOnly?: boolean;
}

export const fetchAkbContent = (filter: AkbContentFilter = {}): Promise<AkbContentRow[]> => {
  const params = new URLSearchParams();
  if (filter.scopeEnv !== undefined) params.set('scopeEnv', filter.scopeEnv);
  if (filter.scopeTier !== undefined) params.set('scopeTier', filter.scopeTier);
  if (filter.adminOnly !== undefined) params.set('adminOnly', String(filter.adminOnly));
  const qs = params.toString();
  return api(`/akb${qs ? '?' + qs : ''}`);
};

export const createAkbContent = (data: Omit<AkbContentRow, 'id' | 'createdAt' | 'updatedAt'>) =>
  api('/akb', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const updateAkbContent = (id: string, data: Partial<Omit<AkbContentRow, 'id' | 'createdAt' | 'updatedAt'>>) =>
  api(`/akb/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const deleteAkbContent = (id: string): Promise<void> =>
  api(`/akb/${id}`, { method: 'DELETE' });

export const reembedAkbContent = (id: string): Promise<{ id: string; embedded: boolean }> =>
  api(`/akb/${id}/reembed`, { method: 'POST' });

export const setSimulatedTier = (userId: string, simulatedTier: string | null): Promise<{ ok: boolean }> =>
  api(`/users/${encodeURIComponent(userId)}/simulated-tier`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ simulatedTier }),
  });

/** Set/clear the simulated credit-balance override (centicredits, or null to clear). */
export const setSimulatedBalance = (userId: string, simulatedBalance: number | null): Promise<{ ok: boolean }> =>
  api(`/users/${encodeURIComponent(userId)}/simulated-balance`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ simulatedBalance }),
  });

// ── Credit Pricing matrix (valko-credits-platform Phase 2) ──────────

export interface CreditPricingRow {
  actionKind: string;
  modelClass: string;
  centicredits: number;
  updatedAt?: string;
}

export const fetchCreditPricing = (): Promise<{ rows: CreditPricingRow[] }> => api('/credits/pricing');

export const upsertCreditPricing = (data: { actionKind: string; modelClass: string; centicredits: number }) =>
  api('/credits/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });

export const deleteCreditPricing = (actionKind: string, modelClass: string) =>
  api(`/credits/pricing/${encodeURIComponent(actionKind)}/${encodeURIComponent(modelClass)}`, {
    method: 'DELETE',
  });

// ── Act-As Persona ────────────────────────────────────────

export const setActAsPersona = (sessionId: string, email: string | null) =>
  api(`/sessions/${sessionId}/act-as`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

// ── Intent Analytics (model-meta-hardening Phase 7) ─────────

export interface IntentDistributionEntry {
  intentType: string;
  count: number;
  pct: number;
}

export interface IntentDistributionResponse {
  sinceDays: number;
  total: number;
  distribution: IntentDistributionEntry[];
}

export interface IntentTransitionEntry {
  from: string;
  to: string;
  count: number;
}

export interface IntentTransitionsResponse {
  sinceDays: number;
  transitions: IntentTransitionEntry[];
}

export const fetchIntentDistribution = (days = 7): Promise<IntentDistributionResponse> =>
  api(`/intent-analytics/distribution?days=${days}`);

export const fetchIntentTransitions = (days = 7): Promise<IntentTransitionsResponse> =>
  api(`/intent-analytics/transitions?days=${days}`);
